/**
 * useMediaAssetObjectUrl — preview autenticado de um MediaAsset (Task 9).
 *
 * `mediaAssetContentUrl` cru num `<img src>` daria 401 — o conteúdo exige
 * `Authorization: Bearer` (sem cookie de sessão), então o browser não injeta
 * o header sozinho. Este hook faz o fetch autenticado
 * (`api.fetchMediaAssetBlob`) e converte em `URL.createObjectURL`, com
 * cleanup (`revokeObjectURL`) no unmount/troca de id — evita vazar memória
 * ao trocar de thumbnail no builder de items ou na tabela da biblioteca.
 *
 * `id === null` → sem fetch, sem URL (item de mídia ainda sem asset escolhido).
 */
import { useEffect, useState } from 'react';

import { api } from '../../lib/api-client';

export function useMediaAssetObjectUrl(id: string | null): {
  url: string | null;
  isLoading: boolean;
  isError: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (id === null) {
      setUrl(null);
      setIsLoading(false);
      setIsError(false);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setIsLoading(true);
    setIsError(false);
    setUrl(null);

    void (async () => {
      try {
        const blob = await api.fetchMediaAssetBlob(id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setIsLoading(false);
      } catch {
        if (cancelled) return;
        setIsError(true);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return { url, isLoading, isError };
}
